import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { isCtrl } from '../../utils/BrowserDetection';
import { closest, generateId, scrollIntoView } from '../../utils/DomUtil';
import { findIndex } from '../../utils/FindIndex';
import { EventUtilsOptOut, mergeEventHandlers, PropsAndElementAttrs } from '../../utils/ReactHelpers';
import { findFocusableItemIndex, findIndexOfItemWithValue, isFocusableItem, isSelectableItem, ListItem } from './ListItemUtils';
import { EllipsedStyleName, ListIsActiveClass, ListStyleName } from './ListStyles';

export { ListItem };

export type ListProps = Pick<React.AriaAttributes, 'aria-labelledby'> & {
  id?: string;
  items: ListItem[];
  /** Invoked with the value of the item on click or of the focused item on SPACE key. */
  onSelect?: (value: string) => void;
  /** When true, allows ENTER key (in addition to SPACE key) to trigger the onSelect callback. */
  selectOnEnterKey?: boolean;
  testId?: string;
} & ({ // single-select mode
  /** The value of the currently selected `ListItem`, if any. Implies a single-select `List`. */
  selectedValue?: string | null;
  selectedValues?: never;
  onSelectAll?: never;
} | { // multi-select mode
  /**
   * The value(s) of the currently selected `ListItem`s. Implies a multiselect `List`.
   * If no items are selected in a multiselectable `List`, pass an empty `Set`.
   */
  selectedValues: Set<string>;
  selectedValue?: never;
  /**
   * Invoked when the user indicates they want to select all items. The action to take is left up to your usage context.
   *
   * In most cases, we suggest this event act as a toggle, i.e. it should select all items if all items are not currently selected,
   * but it should deselect all items if all items are already selected.
   */
  onSelectAll?: () => void;
});

export interface ListState {
  activeItemIndex: number;
}

export const ItemIndexAttr = 'data-itemindex';
export const ItemValueAttr = 'data-itemvalue';

export const ListIsActiveContext = React.createContext<boolean>(false);
ListIsActiveContext.displayName = 'ListIsActiveContext';

function getDefaultActiveItemIndex(selectedValues: ListProps['selectedValue'] | ListProps['selectedValues'], items: ListProps['items']): number {
  if (!selectedValues) {
    return findFocusableItemIndex(items, -1, 1);
  } else if (typeof selectedValues === 'string') {
    return findIndexOfItemWithValue(items, selectedValues);
  } else {
    return items.findIndex(item => isFocusableItem(item) && selectedValues.has(item.value!));
  }
}

function isItemChecked(selectedValues: ListProps['selectedValue'] | ListProps['selectedValues'], item: ListItem): boolean | undefined {
  if (!selectedValues) {
    return undefined;
  } else if (typeof selectedValues === 'string') {
    return (isFocusableItem(item) && item.value === selectedValues) || undefined;
  } else {
    return isFocusableItem(item) && selectedValues.has(item.value!);
  }
}

/**
 * `List` displays a keyboard-navigable list of items similar to an HTML `<select size!=1>`.
 * Items are supplied via an array of `ListItem`, which has properties similar to `<option>`.
 *
 * `List` supports typeahead-focus as described in the WAI-ARIA `listbox` spec.
 * It displays a checkmark next to selected item(s), and displays a background color on the "focused" item
 * -- although only the listbox element itself is actually focusable.
 */
export class List extends React.PureComponent<PropsAndElementAttrs<ListProps>, ListState> {
  private readonly defaultListId: string = generateId();
  private listId = () => this.props.id || this.defaultListId;
  private listElement: HTMLElement | null = null;
  private itemTextContent: string[] = [];
  private typeAheadQuery: string = '';
  private typeAheadTimeoutHandle: number = 0;
  private scrollOnFocus: boolean = true;

  public state: ListState = {
    activeItemIndex: Math.max(0, getDefaultActiveItemIndex(this.props.selectedValues || this.props.selectedValue, this.props.items)),
  };

  /** Gets the index into `props.items` of the pseudo-focused listbox option. */
  public getActiveItemIndex(): number {
    return this.state.activeItemIndex;
  }

  /**
   * Sets the index into `props.items` of the pseudo-focused listbox option.
   * @param activeItemIndex the index into `props.items` of the corresponding option to focus, or `-1` for none
   */
  public setActiveItemIndex(activeItemIndex: number): void {
    if (activeItemIndex === -1 || isFocusableItem(this.props.items[activeItemIndex])) {
      this.setState({ activeItemIndex });
    }
  }

  public scrollItemIndexIntoView(itemIndex: number): void {
    scrollIntoView(document.getElementById(listItemId(this.listId(), itemIndex)), 'nearest');
  }

  public componentDidMount(): void {
    this.memoizeItemTextContent();
  }

  public componentDidUpdate(prevProps: ListProps, prevState: ListState): void {
    const { items } = this.props;
    if (!items) { return; }

    const { activeItemIndex } = this.state;

    if (prevProps.items !== items) {
      this.memoizeItemTextContent();
    }
    if (items.length && activeItemIndex >= items.length) {
      this.setState({ activeItemIndex: items.length - 1 });
      return;
    }
    if (prevState.activeItemIndex !== activeItemIndex) {
      this.scrollItemIndexIntoView(activeItemIndex);
    }
  }

  private handleFocus: React.FocusEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    if (e.target === e.currentTarget && this.scrollOnFocus) {
      this.scrollItemIndexIntoView(this.state.activeItemIndex);
    }
    this.scrollOnFocus = true;
  }, e => this.props.onFocus?.(e));

  private handleMouseDown: React.MouseEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    const activeItemIndex = getTargetedItemIndex(e.target as HTMLElement);
    this.scrollOnFocus = activeItemIndex > -1;
    if (activeItemIndex > -1 && activeItemIndex !== this.state.activeItemIndex) {
      this.setState({ activeItemIndex });
    }
  }, e => this.props.onMouseDown?.(e));

  private handleClick: React.MouseEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    const item = this.props.items[getTargetedItemIndex(e.target as HTMLElement)];
    if (isSelectableItem(item)) {
      this.props.onSelect?.(item.value!);
    }
  }, e => this.props.onClick?.(e));

  private keyDownHandlers: { [key: string]: (e: React.KeyboardEvent<HTMLDivElement>) => number | void } = Object.freeze({
    'ArrowUp': () => findFocusableItemIndex(this.props.items, this.state.activeItemIndex, -1),
    'ArrowDown': () => findFocusableItemIndex(this.props.items, this.state.activeItemIndex, +1),
    'Home': () => findFocusableItemIndex(this.props.items, -1, 1),
    'End': () => findFocusableItemIndex(this.props.items, this.props.items.length, -1),
    ' ': e => {
      const activeItem = this.props.items[this.state.activeItemIndex];
      if (!e.repeat && isSelectableItem(activeItem)) {
        this.props.onSelect?.(activeItem.value!);
      }
    },
    'Enter': e => {
      if (this.props.selectOnEnterKey) {
        this.keyDownHandlers[' '](e);
      }

    },
    'Ctrl+a': () => {
      if (this.props.selectedValues) {
        this.props.onSelectAll?.();
      }
    },
  });
  private handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    const key = (isCtrl(e) ? 'Ctrl+' : '') + e.key;
    if (this.keyDownHandlers[key]) {
      e.stopPropagation();
      e.preventDefault();
      const activeItemIndex = this.keyDownHandlers[key](e);
      if (activeItemIndex !== undefined && activeItemIndex !== this.state.activeItemIndex) {
        this.setState({ activeItemIndex });
      }
    }
  }, e => this.props.onKeyDown?.(e));

  private handleKeyPress: React.KeyboardEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    if (e.key === ' ' || e.key === 'Enter' || e.altKey || e.ctrlKey || e.metaKey) { return; }

    window.clearTimeout(this.typeAheadTimeoutHandle);
    this.typeAheadTimeoutHandle = window.setTimeout(this.resetQuery, 500);
    this.typeAheadQuery += e.key;

    const query = this.typeAheadQuery.toLowerCase();
    const activeItemIndex = findIndex(this.itemTextContent, (text, itemIndex) => {
      return isFocusableItem(this.props.items[itemIndex]) && text.toLowerCase().indexOf(query) === 0;
    }, this.state.activeItemIndex + (query.length === 1 ? 1 : 0));

    if (activeItemIndex >= 0) {
      this.setState({ activeItemIndex });
    }
  }, e => this.props.onKeyPress?.(e));

  private handleListRef = (ref: HTMLElement | null): void => {
    this.listElement = ref;
  }

  private memoizeItemTextContent(): void {
    const elementsRenderedFromItems = this.listElement?.querySelectorAll<HTMLElement>(`[role="option"], [role="group"], [role="separator"]`);
    this.itemTextContent = Array.from(elementsRenderedFromItems || []).map(option => option.textContent || '');
  }

  private resetQuery = (): void => {
    this.typeAheadQuery = '';
  }

  private renderItems(): React.ReactNode[] {
    const { items, selectedValue, selectedValues } = this.props;
    const { activeItemIndex } = this.state;
    const listId = this.listId();

    if (!items) { return []; }

    const setsize = items.filter(isFocusableItem).length;
    const itemNodes: React.ReactNode[] = [];

    for (let index = 0, posinset = 0; index < items.length; index++) {
      const item = items[index];

      if (item.separator) {
        itemNodes.push(<div data-tb-test-id={item.testId} key={`separator%%${index}`} role='separator' />);
      } else {
        if (!item.value) {
          throw new Error(`Item has no value: ${JSON.stringify(item)}`);
        }
        const makeItemElem = (listItem: ListItem) => {
          /**
           * We're (ab)using `aria-selected` to indicate the "focused" item here.
           * There are strongly differing opinions about the mixing of `aria-selected` with `aria-checked`.
           * This pattern is used by SF Lightning Design System and it seems to be the only way to make multi-selection accessibly work with a combobox pattern.
           * Also, historically Safari+VO will not read out changes to `aria-activedescendant` without also setting `aria-selected` on the target option.
           */
          const isActive: true | undefined = index === activeItemIndex || undefined;
          const isChecked = isItemChecked(selectedValues || selectedValue, listItem);

          posinset++; // increment first as aria-posinset starts counting from 1
          return ListItemElem({ index, isSelected: isActive, isChecked, item: listItem, listId, posinset, setsize });
        };

        // this logic has much in common with similar logic in ListMenuWidget::buildMenuFromMenuItemInfos
        if (item.group) {
          const groupLabelId = generateId();
          const groupItemNodes: React.ReactNode[] = [];

          for (index++; isFocusableItem(items[index]); index++) {
            groupItemNodes.push(makeItemElem(items[index]));
          }
          index--;

          itemNodes.push(
            <div role='group' aria-labelledby={groupLabelId} key={`group%%${item.value}`}>
              <div role='presentation' id={groupLabelId} data-tb-test-id={item.testId} key='title'>
                {ListItemContent({ item })}
              </div>
              {groupItemNodes}
            </div>
          );
        } else {
          itemNodes.push(makeItemElem(item));
        }
      }
    }

    return itemNodes;
  }

  public render(): React.ReactNode {
    // eslint-disable-next-line max-len
    const { className, items, onSelect, onSelectAll, selectedValue, selectedValues, selectOnEnterKey, testId, ...htmlAttrs } = this.props;
    const { activeItemIndex } = this.state;

    return <ListIsActiveContext.Consumer>{listIsActive =>
      <div
        {...EventUtilsOptOut}
        tabIndex={0} // List must always be focusable because option nodes are not
        {...htmlAttrs}
        aria-activedescendant={activeItemIndex > -1 ? listItemId(this.listId(), activeItemIndex) : undefined}
        aria-multiselectable={!!selectedValues || undefined}
        className={TypeStyle.classes(ListStyleName, className, { [ListIsActiveClass]: listIsActive })}
        data-tb-test-id={testId}
        onClick={this.handleClick}
        onFocus={this.handleFocus}
        onKeyDown={this.handleKeyDown}
        onKeyPress={this.handleKeyPress}
        onMouseDown={this.handleMouseDown}
        ref={this.handleListRef}
        role='listbox'
      >
        {this.renderItems()}
      </div>
    }</ListIsActiveContext.Consumer>;
  }
}

/** Returns the list index of the clicked item, or -1 if none */
function getTargetedItemIndex(node: HTMLElement): number {
  const itemIndex = parseInt(closest(node, '[role="option"]')?.getAttribute(ItemIndexAttr)!, 10);
  return isNaN(itemIndex) ? -1 : itemIndex;
}

function listItemId(listId: string, itemIndex: number): string {
  return `${listId}-${itemIndex}`;
}

const ListItemElem: React.FC<{ item: ListItem, index: number, isSelected?: boolean, isChecked?: boolean, listId: string, posinset: number, setsize: number }> =
  ({ index, item, isSelected, isChecked, listId, posinset, setsize }) =>
    <div
      aria-checked={isChecked}
      aria-describedby={item['aria-describedby']}
      aria-disabled={item.disabled}
      aria-posinset={posinset}
      aria-selected={isSelected}
      aria-setsize={setsize}
      {...{
        [ItemIndexAttr]: index,
        [ItemValueAttr]: item.value,
      }}
      data-tb-test-id={item.testId}
      key={`item%%${item.value}`}
      id={listItemId(listId, index)}
      role='option'
      title={item.tooltip}
    >
      {ListItemContent({ item })}
    </div>;

const ListItemContent: React.FC<{ item: ListItem }> = ({ item }) =>
  <span className={EllipsedStyleName}>{item.content || item.value}</span>;
